import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribe } from './subscribe';
import { OutputEvent, OutputSnapshot } from './types';
import { isNodeTerminal, watchNodeTerminal } from './executionStreamManager';

const DEBOUNCE_MS = 200;
const FALLBACK_POLL_INTERVAL_MS = 3000;

type Options = {
  onRows: () => void;
  onResync?: () => void;
};

export function useNodeOutputStream(
  executionId: string | null | undefined,
  nodeId: string | null | undefined,
  enabled: boolean,
  { onRows, onResync }: Options,
) {
  const queryClient = useQueryClient();
  const onRowsRef = useRef(onRows);
  const onResyncRef = useRef(onResync);

  useEffect(() => {
    onRowsRef.current = onRows;
    onResyncRef.current = onResync;
  });

  useEffect(() => {
    if (!enabled || !executionId || !nodeId) return;
    if (isNodeTerminal(queryClient, executionId, nodeId)) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        onRowsRef.current();
      }, DEBOUNCE_MS);
    };

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const startFallbackPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => onRowsRef.current(), FALLBACK_POLL_INTERVAL_MS);
    };

    const releaseSubscribe = subscribe<OutputSnapshot, OutputEvent>(
      `/workflow-executions/${executionId}/nodes/${nodeId}/output-data/events`,
      {
        onSnapshot() {
          // initial data comes from the existing paginated fetch
        },
        onEvent(event) {
          if (event.kind === 'output.rows' && event.node_id === nodeId) {
            scheduleRefetch();
          }
        },
        onResync() {
          onResyncRef.current?.();
        },
        onUnavailable: startFallbackPolling,
        onFatal: startFallbackPolling,
      },
    );

    const releaseWatch = watchNodeTerminal(queryClient, executionId, nodeId, () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      releaseSubscribe();
    });

    return () => {
      if (timer) clearTimeout(timer);
      if (pollTimer) clearInterval(pollTimer);
      releaseWatch();
      releaseSubscribe();
    };
  }, [executionId, nodeId, enabled, queryClient]);
}
